// COP Copilot runtime — backend selection (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// LIFTED from takbot services/agent-runtime/src/backends/index.ts (env read via ../env.ts). TDFBot's
// AUTO-FALLBACK semantics: if TAKBOT_LLM_BASE_URL is set AND its /models endpoint is reachable
// (serves >=1 model) we use the OpenAIBackend (→ a local Qwen3.6 /v1 server). Otherwise — unset
// URL, or an unreachable/empty endpoint — we fall back to the deterministic MockBackend, the
// zero-dependency DEFAULT, so the whole COP/build/tests run with NO model server at all.
//
// Mirrors rag-sidecar: the real backend is used only when its health probe passes; any failure
// silently degrades to the mock.

import type { Backend } from "./types.ts";
import { MockBackend } from "./mock.ts";
import { OpenAIBackend } from "./openai.ts";
import { readEnv } from "../env.ts";

export type { Backend, Plan, SessionState, TranscriptEntry } from "./types.ts";
export { MockBackend } from "./mock.ts";
export { OpenAIBackend } from "./openai.ts";

/** Select the active backend. Async because the OpenAI path is gated on a live /models probe.
 *  Always resolves to a usable Backend (never throws). */
export async function selectBackend(): Promise<Backend> {
  // Same-origin model proxy by convention: the deployment serves the local LLM at /copilot/v1
  // (dev: caddy :8443 → llama-server :8090 serving Qwen3.6). An explicit TAKBOT_LLM_BASE_URL
  // overrides it; if the proxy/model isn't up, endpointOk fails below and we degrade to the
  // deterministic MockBackend — so the COP never hard-depends on a running model.
  const baseUrl = readEnv("TAKBOT_LLM_BASE_URL")?.trim() || "/copilot/v1";
  if (baseUrl) {
    const ok = await OpenAIBackend.endpointOk(baseUrl);
    if (ok) {
      return new OpenAIBackend(baseUrl);
    }
    // URL set but unreachable/empty ⇒ degrade to mock.
  }
  return new MockBackend();
}
